'use client'

import { useRef, type CSSProperties, type ReactNode } from 'react'

/**
 * Хөлийн дэвсгэр — cloud админы нэвтрэх хуудасны (`/sign-in`) хэлбэрээр.
 *
 * Зарчим нь тэндхийн `auth-backdrop.tsx`-тэй ЯГ ИЖИЛ:
 *   1. Дэвсгэрт бүдгэрүүлж, өнгийг нь суулгасан зураг;
 *   2. Дээр нь сэдвийн дагуу тодролыг барих нэг давхарга;
 *   3. Хулгана хөдлөхөд түүнийг ДАГАЖ зөөгдөх дугуй «гэрлийн толбо» —
 *      тэр хэсэгт л зураг хурц, өнгөтэйгөөрөө харагдана.
 *
 * ! ТОЛБО НЬ `mask-image`-ЭЭР. Хурц зургийг бүтнээр нь давхарлаад радиал
 * ! градиент маскаар зөвхөн хулганы орчмыг нь нээнэ — ингэснээр `filter`
 * ! дахин тооцоологдохгүй, гүйлгэлт тасалдахгүй.
 *
 * ! Хөдөлгөөн багасгасан төхөөрөмж дээр толбо ОГТ зурагдахгүй
 * ! (`motion-reduce:hidden`) — тасралтгүй хөдөлгөөн зарим хүнд таагүй.
 *
 * ! Зураг `loading='lazy'` — хөл нь хуудасны хамгийн доод хэсэгт. Эс бөгөөс
 * ! React SSR түүнийг `<head>`-д preload болгож (~1.6 МБ), нүүрний видеотой
 * ! зурвасын өргөнөөр өрсөлддөг байв.
 */
export const FooterBackdrop = ({ image, children }: { image: string; children: ReactNode }) => {
  const rootRef = useRef<HTMLDivElement>(null)

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const root = rootRef.current

    if (!root) return

    const rect = root.getBoundingClientRect()

    root.style.setProperty('--spot-x', `${event.clientX - rect.left}px`)
    root.style.setProperty('--spot-y', `${event.clientY - rect.top}px`)
  }

  /* Хулгана ороогүй үед толбыг дэлгэцээс ГАДНА байлгана */
  const offScreen = { '--spot-x': '-100vw', '--spot-y': '-100vh' } as CSSProperties

  return (
    <div
      ref={rootRef}
      onPointerMove={handlePointerMove}
      style={offScreen}
      className='group/backdrop bg-background relative isolate overflow-hidden'
    >
      <div aria-hidden className='pointer-events-none absolute inset-0 -z-30 scale-105'>
        <img
          src={image}
          alt=''
          loading='lazy'
          className='size-full object-cover opacity-90 blur-[7px] saturate-[0.3]'
        />
      </div>

      <div aria-hidden className='bg-muted/75 dark:bg-background/80 pointer-events-none absolute inset-0 -z-20' />

      <div
        aria-hidden
        className='pointer-events-none absolute inset-0 -z-10 scale-105 opacity-0 transition-opacity duration-500 group-hover/backdrop:opacity-100 motion-reduce:hidden'
        style={{
          maskImage: 'radial-gradient(circle 300px at var(--spot-x) var(--spot-y), black 0%, transparent 70%)',
          WebkitMaskImage: 'radial-gradient(circle 300px at var(--spot-x) var(--spot-y), black 0%, transparent 70%)'
        }}
      >
        <img src={image} alt='' loading='lazy' className='size-full object-cover' />
      </div>

      {children}
    </div>
  )
}

export default FooterBackdrop
